<template>
  <div>
    <thread-form-group :label="label" :helper-text="helperText">
      <div class="thread-file-upload">
        <span class="thread-file-upload__name" :class="{ 'thread-file-upload__name--filled': !!fileName }" :title="fileName">
          {{ displayName }}
        </span>
        <label class="thread-file-upload__browse">
          Browse
          <input type="file" class="thread-file-upload__input" v-bind="$attrs" @change="onChange" />
        </label>
      </div>
    </thread-form-group>
    <template v-if="allowCustomName">
      <thread-form-group v-if="fileName" label="Document name" helperText="Do not include Personally Identifiable Information.">
        <input type="text" class="form-control" :value="localDocName" @input="onCustomNameInput" @blur="onCustomNameBlur" />
      </thread-form-group>
    </template>
    <thread-hashed-filename v-else :document-name="documentName" />
  </div>
</template>

<script>
import ThreadFormGroup from "@/components/common/thread-form-group.vue";
import ThreadHashedFilename from "@/components/common/thread-hashed-filename.vue";

import fileHashMixin from "@/mixins/fileHashMixin";

const MAX_DISPLAY_LENGTH = 30;

export default {
  name: "ThreadFileUpload",
  components: { ThreadFormGroup, ThreadHashedFilename },
  mixins: [fileHashMixin],
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: "Document"
    },
    fileName: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Browse for a file"
    },
    existingNames: {
      type: Array,
      default: () => []
    },
    documentName: {
      type: String,
      default: ""
    },
    allowCustomName: {
      type: Boolean,
      default: false
    },
    helperText: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      localDocName: "",
      fileExt: ""
    };
  },
  computed: {
    displayName() {
      if (!this.fileName) {
        return this.placeholder;
      }
      return this.fileName.length > MAX_DISPLAY_LENGTH ? this.fileName.slice(0, MAX_DISPLAY_LENGTH) + "..." : this.fileName;
    }
  },
  watch: {
    fileName(newVal) {
      if (this.allowCustomName) {
        this.localDocName = newVal;
      }
    },
    documentName: {
      immediate: true,
      handler(newVal) {
        if (this.allowCustomName && newVal && !this.localDocName) {
          this.localDocName = newVal;
        }
      }
    }
  },
  methods: {
    onChange(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = null;
      if (!file) {
        return;
      }
      if (this.allowCustomName) {
        const lastDot = file.name.lastIndexOf(".");
        this.fileExt = lastDot === -1 ? "" : file.name.slice(lastDot + 1).toLowerCase();
        this.$emit("change", file, file.name);
      } else {
        const hashed = this.hashFileName(file.name, this.existingNames);
        this.$emit("change", file, hashed);
      }
    },
    onCustomNameInput(event) {
      this.localDocName = event.target.value;
      this.$emit("name-change", this.localDocName);
    },
    onCustomNameBlur() {
      if (!this.fileExt || !this.localDocName) {
        return;
      }
      if (!this.localDocName.toLowerCase().endsWith(`.${this.fileExt}`)) {
        this.localDocName = `${this.localDocName}.${this.fileExt}`;
        this.$emit("name-change", this.localDocName);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.thread-file-upload {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 10px;
  border-radius: 3px;
  border: solid 1px #e4e8eb;
  box-shadow: inset 0 1px 2px 0 #e4e8eb;
  background-color: #ffffff;

  &__name {
    font-size: 14px;
    color: #9b9b9b;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1;
    margin-right: 8px;

    &--filled {
      color: #212b36;
    }
  }

  &__browse {
    flex-shrink: 0;
    padding: 3px 26px;
    border-radius: 4px;
    border: solid 1px #1e8fe1;
    color: #1e8fe1;
    font-size: 14px;
    font-weight: normal;
    cursor: pointer;
    margin: 0;
    white-space: nowrap;
  }

  &__input {
    display: none;
  }
}
</style>
